import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LuDownload, LuSearch } from 'react-icons/lu';

import usePermissions from '../../../components/Menu/MenuItems/hooks/usePermissions';
import {
   exportDunningReconciliation,
   listDunningReconciliation,
} from '../../../services/finance/dunningApi';
import { financeErrorMessage } from '../utils/apiError';
import { formatBRLShort } from '../utils/dunningFormat';

import {
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../styles/layout/PageHeader';
import {
   Card,
   ControlInput,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   FieldLabel,
   SecondaryButton,
} from '../../../styles/dunning/base';
import {
   HeaderActions,
   HeaderRow,
   KpiLabel,
   KpiTile,
   KpiValue,
   PageWrap,
   ShellButton,
} from '../../../styles/dunning/shell';
import {
   FilterField,
   FilterSelect,
   FiltersSpacer,
   LabeledFiltersCard,
   SearchGoButton,
   SearchInput,
   SearchWrap,
} from '../../../styles/dunning/filters';

import CheckChargeModal from '../components/CheckChargeModal';
import ReconciliationTable from './components/ReconciliationTable';
import {
   CHECKED_OPTIONS,
   CHECKED_VALUES,
   EXPORT_ROW_LIMIT,
   RECONCILIATION_PER_PAGE,
   allowsDateRange,
} from './constants';

// ---------------------------------------------------------------------------
// Financeiro › Gestão de cobranças › Acerto com o escritório (handoff 0103).
//
// A LINHA É O TÍTULO, não o caso: é o documento com que o gestor bate o
// relatório do escritório e fecha o valor a pagar pelo serviço. Tela própria
// (não aba da Central) porque o recorte é outro — filtra por data de
// CONFERÊNCIA, lista título a título e serve leitura gerencial, não fila de
// trabalho.
//
// Diferente da Central, esta é uma tela de PESQUISA e segue a `navegacao.md`
// cap.1 inteira: URL é a fonte da verdade, busca só no submit, URL limpa não
// busca, sentinela `applied=1`, `replace: true`. Sem ordenação por cabeçalho —
// o backend não expõe `order_by` aqui (§1.10, item 4).
//
// Permissão `finance:dunning:reconcile` gateia a rota, o botão de conferir e o
// export. Ela é separada de `:interact` de propósito: quem trata os casos —
// inclusive o operador do escritório — não confere o próprio trabalho.
// ---------------------------------------------------------------------------

// Chaves = nomes dos query params = nomes do backend (navegacao.md §1.1).
const DEFAULT_FILTERS = {
   checked: 'all',
   from: '',
   to: '',
   customer_doc: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };

   const rawChecked = searchParams.get('checked');
   if (CHECKED_VALUES.includes(rawChecked)) next.checked = rawChecked;

   ['from', 'to', 'customer_doc'].forEach((key) => {
      const value = searchParams.get(key);
      if (value) next[key] = value;
   });

   // Combinação impossível herdada da URL (§1.0.3): período com "não
   // conferidos" é 422 garantido no backend. Some o período, não a busca.
   if (!allowsDateRange(next.checked)) {
      next.from = '';
      next.to = '';
   }

   return next;
}

// Documento vai SEM máscara para o backend (mesma regra da Central).
function normalizeDoc(raw) {
   return String(raw ?? '').replace(/\D/g, '');
}

// Filtros da tela → argumentos do service. `checked` é tri-estado: 'all' vira
// ausência do param, e o booleano precisa ser booleano de verdade.
function toServiceFilters(filters) {
   return {
      checked: filters.checked === 'all' ? undefined : filters.checked === 'true',
      from: filters.from || undefined,
      to: filters.to || undefined,
      customerDoc: filters.customer_doc || undefined,
   };
}

export default function DunningReconciliation() {
   const { hasPermission } = usePermissions();
   // A rota já exige a chave; isto gateia as AÇÕES (conferir, exportar) para o
   // caso de a permissão ser revogada com a tela aberta.
   const canReconcile = hasPermission('finance:dunning:reconcile');

   const [searchParams, setSearchParams] = useSearchParams();

   // Leitura ÚNICA, no mount (§1.1): mudanças posteriores da URL não
   // sobrescrevem o que o operador digitou. Initializer preguiçoso — roda uma
   // vez só, não a cada render.
   const [initial] = useState(() => ({
      filters: readFiltersFromUrl(searchParams),
      hasQueryParams: searchParams.toString() !== '',
      page: Number(searchParams.get('page')) || 1,
   }));
   const {
      filters: initialFilters,
      hasQueryParams: hasInitialQueryParams,
      page: initialPage,
   } = initial;

   // Rascunho × aplicado (§1.3): os inputs editam o rascunho; só o submit
   // promove a filtro aplicado — e paginação/export usam o APLICADO.
   const [draft, setDraft] = useState(initialFilters);
   const [appliedFilters, setAppliedFilters] = useState(initialFilters);
   const [currentPage, setCurrentPage] = useState(initialPage);

   const [rows, setRows] = useState([]);
   const [summary, setSummary] = useState(null);
   const [meta, setMeta] = useState(null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState(null);
   const [hasSearched, setHasSearched] = useState(hasInitialQueryParams);
   const [isExporting, setIsExporting] = useState(false);
   // Linha do relatório em vias de ser conferida/desconferida.
   const [checkRow, setCheckRow] = useState(null);

   const seqRef = useRef(0);
   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (values, page) => {
         // Sentinela `applied=1` (§1.5): sem ele, uma busca sem filtro nenhum
         // deixaria a URL limpa e o F5 cairia na abertura fria.
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key] && values[key] !== DEFAULT_FILTERS[key]) {
               params[key] = values[key];
            }
         });
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchRows = useCallback(async (values, page) => {
      seqRef.current += 1;
      const seq = seqRef.current;
      setLoading(true);
      setError(null);

      try {
         const result = await listDunningReconciliation({
            ...toServiceFilters(values),
            page,
            perPage: RECONCILIATION_PER_PAGE,
         });
         if (seqRef.current !== seq) return;
         setRows(result.charges);
         setSummary(result.summary);
         setMeta(result.meta);
         setLoading(false);
      } catch (err) {
         if (seqRef.current !== seq) return;
         setLoading(false);
         setError(err);
         toast.error(
            financeErrorMessage(err, 'Falha ao carregar o acerto.')
         );
      }
   }, []);

   // Fetch no mount SÓ com query params (§1.4): URL limpa = abertura fria =
   // tela vazia convidando a pesquisar.
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchRows(initialFilters, initialPage);
   }, [hasInitialQueryParams, initialFilters, initialPage, fetchRows]);

   const handleSearch = useCallback(() => {
      const values = {
         ...draft,
         customer_doc: normalizeDoc(draft.customer_doc),
      };
      setDraft(values);
      setAppliedFilters(values);
      setCurrentPage(1);
      setHasSearched(true);
      commitToUrl(values, 1);
      fetchRows(values, 1);
   }, [draft, commitToUrl, fetchRows]);

   // §1.7: zera rascunho, aplicados, resultado e URL — SEM disparar busca.
   const handleClear = useCallback(() => {
      setDraft(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setCurrentPage(1);
      setRows([]);
      setSummary(null);
      setMeta(null);
      setError(null);
      setHasSearched(false);
      setSearchParams({}, { replace: true });
   }, [setSearchParams]);

   // Paginação usa o APLICADO, nunca o rascunho (§1.3).
   const handlePageChange = useCallback(
      (page) => {
         setCurrentPage(page);
         commitToUrl(appliedFilters, page);
         fetchRows(appliedFilters, page);
      },
      [appliedFilters, commitToUrl, fetchRows]
   );

   // Trocar para "só não conferidos" apaga o período: a combinação é 422 no
   // backend porque `from`/`to` filtram a data da conferência.
   const handleCheckedChange = useCallback((value) => {
      setDraft((prev) => ({
         ...prev,
         checked: value,
         from: allowsDateRange(value) ? prev.from : '',
         to: allowsDateRange(value) ? prev.to : '',
      }));
   }, []);

   const handleExport = useCallback(async () => {
      setIsExporting(true);
      try {
         // Exporta o MESMO recorte que está na tela (aplicado, não rascunho) —
         // sem paginação, é o backend que varre tudo.
         const blob = await exportDunningReconciliation(
            toServiceFilters(appliedFilters)
         );
         const url = window.URL.createObjectURL(new Blob([blob]));
         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', `acerto_cobranca_${Date.now()}.xlsx`);
         document.body.appendChild(link);
         link.click();
         link.remove();
         window.URL.revokeObjectURL(url);

         if ((meta?.total ?? 0) > EXPORT_ROW_LIMIT) {
            toast.warn(
               `O recorte tem mais de ${EXPORT_ROW_LIMIT.toLocaleString('pt-BR')} títulos. A planilha traz um aviso de truncamento no final — estreite o período.`
            );
         }
      } catch {
         // NÃO usa `financeErrorMessage` aqui: com `responseType: 'blob'`, o
         // corpo de erro chega como Blob e o leitor de JSend não acha `code`
         // nem `message` — o toast viraria "Request failed with status code
         // 422". Mensagem fixa é mais honesta do que o texto cru do axios.
         toast.error(
            'Não foi possível exportar o acerto. Tente novamente ou estreite o período.'
         );
      } finally {
         setIsExporting(false);
      }
   }, [appliedFilters, meta]);

   // Conferir muda o estado da linha — e ela pode SAIR do recorte filtrado
   // (ex.: filtro "só não conferidos"). Por isso refaz a busca em vez de
   // remendar a lista no cliente.
   const handleChecked = useCallback(() => {
      fetchRows(appliedFilters, currentPage);
   }, [appliedFilters, currentPage, fetchRows]);

   const dateRangeEnabled = allowsDateRange(draft.checked);

   let content;
   if (!hasSearched && !loading) {
      content = (
         <Card>
            <EmptyWrap>
               <EmptyTitle>Pesquise para montar o acerto</EmptyTitle>
               <EmptyText>
                  Escolha o recorte e clique em Pesquisar. O período filtra a
                  data da conferência, não o vencimento nem o pagamento.
               </EmptyText>
            </EmptyWrap>
         </Card>
      );
   } else if (error && !rows.length && !loading) {
      content = (
         <Card>
            <EmptyWrap>
               <EmptyTitle>Algo deu errado</EmptyTitle>
               <EmptyText>Falha ao carregar o acerto.</EmptyText>
               <div style={{ marginTop: 12 }}>
                  <SecondaryButton
                     type="button"
                     onClick={() => fetchRows(appliedFilters, currentPage)}
                  >
                     Tentar novamente
                  </SecondaryButton>
               </div>
            </EmptyWrap>
         </Card>
      );
   } else {
      content = (
         <ReconciliationTable
            rows={rows}
            meta={meta}
            loading={loading}
            canReconcile={canReconcile}
            onPageChange={handlePageChange}
            onAskCheck={setCheckRow}
         />
      );
   }

   return (
      <PageWrap>
         <HeaderRow>
            <PageHeaderText>
               <PageTitle>Relatório cobrança externa</PageTitle>
               <PageSubtitle>
                  Títulos que foram cobrados pelo escritório e já entraram.
                  Confira linha a linha e feche o valor do serviço.
               </PageSubtitle>
            </PageHeaderText>
            <HeaderActions>
               {/* O `summary` é do conjunto filtrado INTEIRO, não da página —
                   é o número que o gestor leva para pagar o escritório
                   (0103 §5.5). Por isso fica em destaque, e não no rodapé. */}
               <KpiTile>
                  <KpiLabel>Títulos no acerto</KpiLabel>
                  <KpiValue>{loading ? '—' : summary?.count ?? '—'}</KpiValue>
               </KpiTile>
               <KpiTile>
                  <KpiLabel>Total do acerto</KpiLabel>
                  <KpiValue>
                     {loading || !summary
                        ? '—'
                        : formatBRLShort(summary.totalAmount)}
                  </KpiValue>
               </KpiTile>
               {canReconcile && (
                  <ShellButton
                     type="button"
                     disabled={!hasSearched || isExporting || loading}
                     title={
                        hasSearched
                           ? 'Exportar o recorte atual em .xlsx'
                           : 'Pesquise antes de exportar'
                     }
                     onClick={handleExport}
                  >
                     <LuDownload size={18} />
                     {isExporting ? 'Exportando...' : 'Exportar'}
                  </ShellButton>
               )}
            </HeaderActions>
         </HeaderRow>

         <LabeledFiltersCard>
            <SearchWrap>
               <LuSearch size={16} />
               <SearchInput
                  value={draft.customer_doc}
                  placeholder="CNPJ/CPF do cliente"
                  title="Pressione Enter ou use Pesquisar"
                  onChange={(event) => {
                     const { value } = event.target;
                     setDraft((prev) => ({ ...prev, customer_doc: value }));
                  }}
                  onKeyDown={(event) => {
                     if (event.key === 'Enter') handleSearch();
                  }}
               />
            </SearchWrap>

            {/* Controles rotulados: "dd/mm/aaaa" sozinho não diz QUE data é
                (e aqui a resposta não é óbvia — é a data da conferência, não a
                do vencimento nem a do pagamento). O título completo fica no
                `title`, que o rótulo curto não comporta. */}
            <FilterField>
               <FieldLabel htmlFor="reconciliation-checked">
                  Status conferência
               </FieldLabel>
               <FilterSelect
                  id="reconciliation-checked"
                  value={draft.checked}
                  onChange={(event) => handleCheckedChange(event.target.value)}
               >
                  {CHECKED_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </FilterSelect>
            </FilterField>

            <FilterField>
               <FieldLabel htmlFor="reconciliation-from">
                  Data inicial
               </FieldLabel>
               <ControlInput
                  id="reconciliation-from"
                  type="date"
                  style={{ width: 160 }}
                  value={draft.from}
                  disabled={!dateRangeEnabled}
                  title={
                     dateRangeEnabled
                        ? 'Conferido a partir de'
                        : 'O período filtra a data da conferência — não se aplica a títulos não conferidos'
                  }
                  onChange={(event) => {
                     const { value } = event.target;
                     setDraft((prev) => ({ ...prev, from: value }));
                  }}
               />
            </FilterField>

            <FilterField>
               <FieldLabel htmlFor="reconciliation-to">Data final</FieldLabel>
               <ControlInput
                  id="reconciliation-to"
                  type="date"
                  style={{ width: 160 }}
                  value={draft.to}
                  disabled={!dateRangeEnabled}
                  title={
                     dateRangeEnabled
                        ? 'Conferido até'
                        : 'O período filtra a data da conferência — não se aplica a títulos não conferidos'
                  }
                  onChange={(event) => {
                     const { value } = event.target;
                     setDraft((prev) => ({ ...prev, to: value }));
                  }}
               />
            </FilterField>

            <SearchGoButton type="button" onClick={handleSearch}>
               <LuSearch size={15} />
               Pesquisar
            </SearchGoButton>

            <SecondaryButton
               type="button"
               style={{ height: 36 }}
               onClick={handleClear}
            >
               Limpar
            </SecondaryButton>

            <FiltersSpacer />
         </LabeledFiltersCard>

         {content}

         <CheckChargeModal
            isOpen={!!checkRow}
            dunningId={checkRow?.dunning_id ?? null}
            chargeId={checkRow?.charge_id ?? null}
            checked={!!checkRow?.checked}
            label={
               checkRow?.installment_number
                  ? `Parcela ${checkRow.installment_number}/${checkRow.installment_count}`
                  : 'Título avulso'
            }
            value={checkRow?.value ?? null}
            paymentDate={checkRow?.payment_date ?? null}
            onClose={() => setCheckRow(null)}
            onConfirmed={handleChecked}
         />
      </PageWrap>
   );
}
